// components/Layout.js
import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar";
import Header from "../Header";
import PropTypes from "prop-types";
import SimpleSidebar from "../SimpleSidebar"; // Adjust the path based on your project structure
import { getToken } from "../../utils/tokenStorage"; // Function to get the token
import { useSelector } from "react-redux";
import { getUserInformation } from "../../store/userRole";
import { validateToken, logout } from "../../utils/authService";
import { useDispatch } from "react-redux";
import { resetCategories } from "../../store/categoriesSlice";
import ErrorBoundary from "../ErrorBoundary";

const Layout = ({
  children,
  isSecondSidebarOpen,
  secondSidebarPages,
  questionSidebar,
}) => {
  const [isMobileView, setIsMobileView] = useState(false);
  const navigate = useNavigate();
  const userInformation = useSelector(getUserInformation);
  const storeDispatch = useDispatch();
  const sidebarVisible = useSelector((state) => state.sidebar.visible);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 990);
    };

    handleResize(); // Set initial value

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const checkTokenStatus = async () => {
      try {
        const token = await getToken();
        if (token) {
          const response = await validateToken(token);
          if (!response.ok) {
            storeDispatch(resetCategories());
            if (
              response.error === "Please provide 'token' to validate" ||
              response.error === "Invalid token" ||
              response.error === "Invalid token. User not found" ||
              response.error === "Invalid or expired token" ||
              response.error ===
                "The User is inactive. Please contact your admin."
            ) {
              logout();
            }
          }
        }
        // No token: do nothing — PrivateRoute handles the redirect to /login
        // with the correct redirect param preserved.
      } catch (error) {
        console.error("Error checking token status:", error);
        storeDispatch(resetCategories());
      }
    };

    // Perform an initial check
    checkTokenStatus();

    // Set up interval to check every 2 minutes
    const intervalId = setInterval(checkTokenStatus, 120000); // 120000 milliseconds = 2 minutes

    return () => clearInterval(intervalId); // Clear interval on component unmount
  }, [userInformation, navigate, storeDispatch]);

  return (
    <Container fluid>
      <Row>
        {/* Sidebar - Only render if visible */}
        {sidebarVisible && (
          <Col
            lg={2}
            className={`sidebar ${
              isSecondSidebarOpen ? "d-flex p-0 align-items-start" : ""
            }`}
          >
            <div
              className={`${isMobileView ? "" : "sticky-sidebar-styles"}`}
              style={{ display: `${isSecondSidebarOpen ? "flex" : ""}` }}
            >
              <ErrorBoundary>
                <Sidebar
                  isMobileView={isMobileView}
                  isSecondSidebarOpen={isSecondSidebarOpen}
                />
              </ErrorBoundary>
              {isSecondSidebarOpen && !isMobileView && (
                <SimpleSidebar
                  pages={secondSidebarPages}
                  isOpen={isSecondSidebarOpen}
                  questionSidebar={questionSidebar}
                />
              )}
            </div>
          </Col>
        )}

        {/* Main Content - Expand to full width when sidebar is hidden */}
        <Col lg={sidebarVisible ? 10 : 12} className="p-0" style={{ height: "100vh" }}>
          {sidebarVisible && <Header isMobileView={isMobileView} />}
          <Col lg={12} className={isMobileView ? "p-2" : (sidebarVisible ? "p-5" : "p-0")}>
            {children}
          </Col>
        </Col>
      </Row>
    </Container>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  isSecondSidebarOpen: PropTypes.bool,
  secondSidebarPages: PropTypes.array,
};

export default Layout;
